/**
 * Admin Legal Documents Management
 * Manage T&C, Privacy Policy, Cookie Policy with versioning
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApiGet, adminApiPatch } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowSquareOut,
  Calendar,
  CheckCircle,
  CircleNotch,
  Eye,
  FileText,
  FloppyDisk,
  PencilSimple,
  Warning,
  X
} from '@phosphor-icons/react';
const LegalManagement = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    content: '',
    version: '',
    change_log: '',
    status: 'draft',
  });

  const documentTypes = [
    { type: 'terms', name: 'Terms of Service', icon: FileText },
    { type: 'privacy', name: 'Privacy Policy', icon: FileText },
    { type: 'cookie', name: 'Cookie Policy', icon: FileText },
    { type: 'ai_disclaimer', name: 'AI Use & Disclaimer', icon: FileText },
    { type: 'affiliate_terms', name: 'Affiliate Terms', icon: FileText },
  ];

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/legal');
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error fetching legal documents:', error);
      toast.error('Failed to load legal documents');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (doc) => {
    setSelectedDoc(doc);
    setFormData({
      content: doc.content || '',
      version: doc.version || '1.0',
      change_log: '',
      status: doc.status || 'draft',
    });
    setEditing(true);
  };

  const handleSave = async (publish = false) => {
    if (!selectedDoc || !formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    try {
      setSaving(true);
      
      // Increment version on publish
      let newVersion = formData.version;
      if (publish) {
        const versionParts = formData.version.split('.');
        versionParts[1] = (parseInt(versionParts[1]) + 1).toString();
        newVersion = versionParts.join('.');
      }

      await adminApiPatch(`/api/admin/legal/${selectedDoc.type}`, {
        content: formData.content,
        version: newVersion,
        change_log: formData.change_log,
        status: publish ? 'published' : 'draft',
      });

      toast.success(publish ? 'Document published successfully' : 'Document saved as draft');
      setEditing(false);
      setSelectedDoc(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error(error.message || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const getDraftCount = () => documents.filter(d => d.status === 'draft').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Legal Documents</h1>
        <p className="text-gray-400">Manage platform legal documents with version control</p>
      </div>

      {/* Draft Warning */}
      {getDraftCount() > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
          <Warning size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-yellow-500 font-semibold mb-1">Unpublished Changes</h3>
            <p className="text-sm text-yellow-200/80">
              {getDraftCount()} document{getDraftCount() > 1 ? 's have' : ' has'} unpublished changes.
              Review and publish to make them live.
            </p>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="grid grid-cols-1 gap-4">
        {documentTypes.map((docType) => {
          const doc = documents.find(d => d.type === docType.type);
          const Icon = docType.icon;

          return (
            <motion.div
              key={docType.type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-blue-400" />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{docType.name}</h3>
                      {doc && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          doc.status === 'published'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {doc.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      )}
                    </div>

                    {doc ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Last updated: {new Date(doc.updated_at).toLocaleDateString()}
                          </span>
                          <span>Version {doc.version}</span>
                        </div>
                        {doc.change_log && (
                          <p className="text-xs text-gray-500">Latest: {doc.change_log}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">Not yet created</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {doc && doc.status === 'published' && (
                    <a
                      href={`/legal/${doc.type}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="View Public Page"
                    >
                      <ArrowSquareOut size={18} />
                    </a>
                  )}
                  <button
                    onClick={() => handleEdit(doc || { type: docType.type, content: '', version: '1.0', status: 'draft' })}
                    className="px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors flex items-center gap-2"
                  >
                    <PencilSimple size={16} />
                    {doc ? 'Edit' : 'Create'}
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Editor Modal */}
      <AnimatePresence>
        {editing && selectedDoc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !saving && setEditing(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#1A1B1E] border border-white/10 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="border-b border-white/10 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">
                    {documentTypes.find(d => d.type === selectedDoc.type)?.name}
                  </h2>
                  <p className="text-sm text-gray-400">Version {formData.version} • {formData.status}</p>
                </div>
                <button
                  onClick={() => !saving && setEditing(false)}
                  disabled={saving}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Legal Warning */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                  <Warning size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-200/80">
                    <strong>Legal Notice:</strong> Editing legal documents should be done in consultation
                    with legal counsel. Published changes take effect immediately.
                  </p>
                </div>

                {/* Content Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Document Content (Markdown Supported)
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter legal document content..."
                    rows={20}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50 resize-none font-mono text-sm"
                  />
                </div>

                {/* Change Log */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Change Log (What changed in this version?)
                  </label>
                  <input
                    type="text"
                    value={formData.change_log}
                    onChange={(e) => setFormData({ ...formData, change_log: e.target.value })}
                    placeholder="e.g., Updated data retention policy"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="border-t border-white/10 p-6 flex items-center justify-between">
                <button
                  onClick={() => !saving && setEditing(false)}
                  disabled={saving}
                  className="px-4 py-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSave(false)}
                    disabled={saving}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
                    Save Draft
                  </button>
                  <button
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 font-semibold"
                  >
                    {saving ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    Publish
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LegalManagement;
